import { useEffect, useMemo, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { fetchProjectLookup, fetchTeamMemberLookup } from '@/lib/entity-api';

/**
 * Everything PurchaseOrderForm needs besides the PO itself: the company's
 * active projects, its team (users), its vendors and the current user's
 * display name. One implementation, used by the Purchase Orders page and by
 * every place that opens the PO form (e.g. "Create PO" on a Warranty).
 * All of it comes from the lookups API (/api/lookups/*).
 */
export function usePurchaseOrderFormOptions() {
  const { companyId, user } = useUser();
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [currentAuthor, setCurrentAuthor] = useState<string>("Unknown User");
  const projectIdToName = useMemo(() => new Map(projects.map((p) => [p.id, p.name] as const)), [projects]);
  const vendorIdToName = useMemo(() => new Map(vendors.map((v) => [v.id, v.name] as const)), [vendors]);

  useEffect(() => {
    if (companyId && user) {
      (async () => {
        try {
          const [projData, members] = await Promise.all([fetchProjectLookup(), fetchTeamMemberLookup()]);
          setProjects(projData);
          setUsers(members.map((m) => ({ id: m.id, name: m.full_name || m.id })));
          setVendors(
            members
              .filter((m) => m.role === 'vendor')
              .map((m) => ({ id: m.id, name: m.company_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() }))
          );
          const me = members.find((m) => m.user_id === user.id);
          const fullNameFromMeta = (user.user_metadata as any)?.full_name as string | undefined;
          setCurrentAuthor(me?.full_name || fullNameFromMeta || user.email || 'Unknown User');
        } catch (error) {
          console.error('Error loading purchase order form options:', error);
        }
      })();
    }
  }, [companyId, user]);

  return { projects, users, vendors, currentAuthor, projectIdToName, vendorIdToName };
}
